/* eslint-disable @typescript-eslint/no-use-before-define */
import 'styled-components/macro'
import { Fragment, type MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isPlainObject } from 'remeda'
import { ThemeProvider } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'
import { QuickAdd } from '@components/QuickAdd/QuickAdd'
import { QuickAddDropdown } from '@components/QuickAddDropdown/QuickAddDropdown'
import { VariantsSlider } from '@components/VariantsSlider/VariantSlider'
import { WishlistCTA } from '@components/WishlistCTA/WishlistCTA'
import { dataTestIds } from '@velasca/constants'
import { useCart } from '@velasca/hooks/use-cart'
import type {
  ApparelProductProps,
  Product,
  ProductSize,
  Status,
} from '@velasca/types'
import { QuickAddToCartDesktop } from '@velasca/ui/quick-add-to-cart-desktop'
import { VerticalTextSeparator } from '@velasca/ui/vertical-text-separator'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { getCurrentCategory } from '@velasca/utils/get-current-category'
import { getSizeGuideUrl } from '@velasca/utils/get-size-guide-url'
import { getThemeByBrand } from '@velasca/utils/get-theme-by-brand'
import { handleError } from '@velasca/utils/handle-error'
import { setQueryParams } from '@velasca/utils/set-query-params'
import ApparelProductSkeleton from '../ApparelProductSkeleton'
import { useExchangeRates } from '../ExchangeRatesProvider'
import {
  AbsoluteQuickAddToCartButton,
  ActionsContainer,
  ApparelCard,
  Description,
  DescriptionBlock,
  DescriptionBold,
  DescriptionThin,
  FrontImgWrapper,
  HoverImg,
  HoverImgWrapper,
  Images,
  Img,
  Link,
  LinkFlex,
  Price,
  ProductDescriptionsWrapper,
  ProductDetailsWrapper,
  ProductInfo,
  Season,
  SeasonLabel,
  SlideUp,
  StyledPriceRow,
  StyledProductBranding,
  Title,
  UpperSection,
} from './ApparelProduct.styles'

export const ApparelProduct = ({
  layout = 'card',
  title,
  showProductType = false,
  productType,
  isGiftCard = false,
  variants = [],
  defaultSelected = 0,
  loading = false,
  showSingleVariant = false,
  sizes,
  showSeason = false,
  openInNewTab = false,
  isQuickAdd = false,
  onQuickAdd,
  showBrandingOnWornImage = false,
  disableBranding = false,
  onAddToCart,
  shouldOpenCartDrawerOnAddToCart = true,
  showVariantSelector = true,
  frontImageToShow = 'standard',
  showWishlistCTA = true,
  productListRef,
}: ApparelProductProps) => {
  const { t } = useTranslation('product')

  const isPLP = Boolean(getCurrentCategory(window.location.pathname))

  const [isQuickAddPopupOpen, setIsQuickAddPopupOpen] = useState<boolean>(false)
  const [isQuickAddDesktopOpen, setIsQuickAddDesktopOpen] =
    useState<boolean>(false)

  const { convert } = useExchangeRates()
  const isSmallerThan768px = useMediaQuery('(max-width: 767px)')

  const [selectedVariant, setSelectedVariant] = useState(
    variants[defaultSelected],
  )

  const sizeGuideUrl = useMemo(
    () => getSizeGuideUrl(selectedVariant?.handle),
    [selectedVariant],
  )

  const description = selectedVariant?.description
  const jolly = selectedVariant?.jolly ?? selectedVariant?.sole_tag?.value

  const price = useMemo(() => {
    const minPrice = isPlainObject(selectedVariant?.price)
      ? selectedVariant?.price?.min * 100
      : 0
    const maxPrice = isPlainObject(selectedVariant?.price)
      ? selectedVariant?.price?.max * 100
      : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [selectedVariant, convert])

  const compareAtPrice = useMemo(() => {
    const minPrice = isPlainObject(selectedVariant?.compareAtPrice)
      ? selectedVariant?.compareAtPrice?.min * 100
      : 0
    const maxPrice = isPlainObject(selectedVariant?.compareAtPrice)
      ? selectedVariant?.compareAtPrice?.max * 100
      : 0

    if (minPrice !== maxPrice) {
      return `${convert(minPrice)} - ${convert(maxPrice)}`
    }

    return convert(maxPrice)
  }, [selectedVariant, convert])

  const isPriceDiscounted = useMemo(() => {
    const nomalizedCompareAtPrice = isPlainObject(
      selectedVariant?.compareAtPrice,
    )
      ? selectedVariant?.compareAtPrice?.min
      : 0
    const nomalizedPrice = isPlainObject(selectedVariant?.price)
      ? selectedVariant?.price?.max
      : 0

    return nomalizedCompareAtPrice > nomalizedPrice
  }, [selectedVariant])

  if (loading) {
    return (
      <ApparelProductSkeleton
        showProductType={showProductType}
        showSingleVariant={showSingleVariant}
        showSeason={showSeason}
        layout={layout === 'card' ? 'card' : 'list'}
      />
    )
  }

  const hasSole = Boolean(description?.bold && description.thin)

  return (
    <ThemeProvider theme={getThemeByBrand()}>
      <ApparelCard
        className={`apparel-product-card ${layout}`}
        layout={layout}
        $isQuickAdd={isQuickAdd}
        onMouseEnter={() => {
          if (!isQuickAdd) {
            return
          }

          setIsQuickAddDesktopOpen(true)
          !isQuickAddDesktopOpen &&
            !isSmallerThan768px &&
            onQuickAdd?.(selectedVariant.handle ?? '')
        }}
        onMouseLeave={() => {
          if (!isQuickAdd) {
            return
          }
          setIsQuickAddDesktopOpen(false)
        }}
      >
        <UpperSection>
          {selectedVariant.handle ? (
            <Link
              href={`/products/${selectedVariant.handle}`}
              target={openInNewTab ? '_blank' : '_self'}
            >
              <Images>
                {showWishlistCTA &&
                  (layout === 'card' || layout === 'search') && (
                    <WishlistCTA
                      layout="tile"
                      productData={{
                        handle: selectedVariant?.handle ?? '',
                        productId: String(selectedVariant?.id ?? ''),
                        title: title || '',
                        price,
                        brand: selectedVariant?.collectionBrandName,
                        origin: 'Suggested products',
                      }}
                    />
                  )}
                {variants.map(({ id, img, collectionBrandName }) => {
                  let frontImage = img?.front
                  let hoverImage = img?.hover

                  if (frontImageToShow === 'reverse') {
                    frontImage = img?.hover
                    hoverImage = img?.front
                  }

                  return (
                    <Fragment key={id}>
                      {
                        //removed with redesign. kept as reference for future re-implementation
                        /* {!collectionBrandName && staffPickIcon?.image && (
                        <StaffPickImage
                          visible={selectedVariant.id === id}
                          alt={staffPickIcon?.image?.alt}
                          imgSrc={setQueryParams(staffPickIcon?.image?.src, [
                            { name: 'width', value: '100' },
                          ])}
                          sources={buildDprSources(staffPickIcon?.image?.src, [
                            { width: 100, dpr: 1 },
                            { width: 200, dpr: 2 },
                            { width: 300, dpr: 3 },
                          ])}
                        />
                      )} */
                      }
                      {hoverImage && (
                        <HoverImgWrapper visible={selectedVariant.id === id}>
                          <HoverImg
                            lazyload
                            lqip
                            showDefault
                            key={`hover-render-${frontImageToShow}`}
                            draggable={false}
                            sources={buildIntrinsicWidthSources({
                              src: hoverImage,
                            })}
                            imgSrc={setQueryParams(hoverImage, [
                              { name: 'width', value: '30' },
                            ])}
                            alt={t('apparelProductHoverImgAltTemplate', {
                              material: description?.thin,
                              color: description?.bold,
                              productTitle: title,
                              productType,
                            })}
                          />
                          {!disableBranding &&
                            showBrandingOnWornImage &&
                            collectionBrandName && (
                              <StyledProductBranding
                                title={collectionBrandName}
                              />
                            )}
                        </HoverImgWrapper>
                      )}
                      <FrontImgWrapper
                        visible={selectedVariant.id === id}
                        className={!img?.hover ? 'single' : ''}
                      >
                        {frontImage && (
                          <>
                            <Img
                              lazyload
                              lqip
                              showDefault
                              key={`front-render-${frontImageToShow}`}
                              draggable={false}
                              transitionDuration={50}
                              imgSrc={setQueryParams(frontImage, [
                                { name: 'width', value: '30' },
                              ])}
                              sources={buildIntrinsicWidthSources({
                                src: frontImage,
                              })}
                              alt={t('apparelProductFrontImgAltTemplate', {
                                material: description?.thin,
                                color: description?.bold,
                                productTitle: title,
                                productType,
                              })}
                            />
                            {!disableBranding &&
                              !showBrandingOnWornImage &&
                              collectionBrandName && (
                                <StyledProductBranding
                                  title={collectionBrandName}
                                />
                              )}
                          </>
                        )}
                      </FrontImgWrapper>
                    </Fragment>
                  )
                })}
              </Images>
            </Link>
          ) : null}
          {!isSmallerThan768px && layout === 'card' && isQuickAdd && (
            <QuickAddItemDesktopWrapper
              shouldOpenCartDrawerOnAddToCart={shouldOpenCartDrawerOnAddToCart}
              title={t('quickAdd')}
              sizeGuideUrl={sizeGuideUrl}
              sizeGuideText={t('sizeLink')}
              sizes={sizes}
              isOpen={isQuickAddDesktopOpen}
              isGiftCard={isGiftCard}
              handle={selectedVariant.handle}
              onAddToCart={onAddToCart}
            />
          )}
        </UpperSection>
        <LinkFlex
          layout={layout}
          hasSole={hasSole}
          href={`/products/${selectedVariant.handle ?? ''}`}
          target={openInNewTab ? '_blank' : '_self'}
        >
          <ProductDetailsWrapper layout={layout}>
            {showWishlistCTA && layout === 'condensed-list' && (
              <WishlistCTA
                layout="absolute-button"
                productData={{
                  handle: selectedVariant?.handle ?? '',
                  productId: String(selectedVariant?.id ?? ''),
                  title: title || '',
                  price,
                  brand: selectedVariant?.collectionBrandName,
                }}
              />
            )}
            <ProductDescriptionsWrapper layout={layout}>
              {showProductType && productType && (
                <ProductInfo
                  layout={layout}
                  isPriceDiscounted={isPriceDiscounted}
                >
                  {productType}
                </ProductInfo>
              )}
              <Title layout={layout}>{title}</Title>

              {(description || jolly) && layout !== 'search' && (
                <Description layout={layout}>
                  {description?.thin && (
                    <DescriptionBold>{description.thin}</DescriptionBold>
                  )}

                  {hasSole && <VerticalTextSeparator className="separator" />}

                  {description?.bold && (
                    <DescriptionThin>{description?.bold}</DescriptionThin>
                  )}

                  {showSeason &&
                    selectedVariant?.seasonID &&
                    selectedVariant?.seasonName && (
                      <Season>
                        <SeasonLabel>{selectedVariant.seasonName}</SeasonLabel>
                      </Season>
                    )}

                  {!isPLP && jolly && (
                    <DescriptionBlock>{jolly}</DescriptionBlock>
                  )}
                </Description>
              )}
            </ProductDescriptionsWrapper>

            {layout !== 'search' && (
              <StyledPriceRow
                layout={layout}
                className="apparel-product-price-row"
              >
                {isPriceDiscounted && (
                  <Price
                    isDiscounted
                    dangerouslySetInnerHTML={{
                      __html: compareAtPrice,
                    }}
                  />
                )}
                <Price
                  data-testid={dataTestIds.PRODUCT_PRICE_IN_CARD}
                  dangerouslySetInnerHTML={{
                    __html: price,
                  }}
                />
              </StyledPriceRow>
            )}
          </ProductDetailsWrapper>
          {isQuickAdd &&
            (layout === 'list' ||
              layout === 'condensed-list' ||
              layout === 'ultra-condensed-list') &&
            !isSmallerThan768px && (
              <ActionsContainer>
                <QuickAddDropdown
                  sizes={sizes}
                  handle={selectedVariant.handle}
                  productListRef={productListRef}
                  condensedLayout={
                    layout === 'condensed-list' ||
                    layout === 'ultra-condensed-list'
                  }
                  shouldOpenCartDrawerOnAddToCart={
                    shouldOpenCartDrawerOnAddToCart
                  }
                  onAddToCart={onAddToCart}
                />
                {showWishlistCTA &&
                  (layout === 'list' || layout === 'ultra-condensed-list') && (
                    <WishlistCTA
                      layout="button"
                      productData={{
                        handle: selectedVariant?.handle ?? '',
                        productId: String(selectedVariant?.id ?? ''),
                        title: title || '',
                        price,
                        brand: selectedVariant?.collectionBrandName,
                        origin: 'Buy the look',
                      }}
                    />
                  )}
              </ActionsContainer>
            )}
        </LinkFlex>

        {layout === 'card' && showVariantSelector && (
          <VariantsSlider
            title={title || ''}
            variants={variants}
            setSelectedVariant={setSelectedVariant}
            selectedVariant={selectedVariant}
            breakpoints={{
              390: { slidesPerView: 5.25 },
              475: { slidesPerView: 6.25 },
              768: { slidesPerView: 4, spaceBetween: 5 },
            }}
          />
        )}
        {isSmallerThan768px && onQuickAdd && selectedVariant?.handle && (
          <AbsoluteQuickAddToCartButton
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.preventDefault()
              setIsQuickAddPopupOpen(true)
              onQuickAdd(selectedVariant.handle ?? '')
            }}
          />
        )}
      </ApparelCard>
      {isQuickAdd && (
        <QuickAdd
          shouldOpenCartDrawerOnAddToCart
          compareAtPrice={compareAtPrice}
          isPriceDiscounted={isPriceDiscounted}
          title={title || ''}
          price={price}
          productType={productType}
          description={description}
          isGiftCard={isGiftCard}
          sizes={sizes}
          handle={selectedVariant.handle}
          isOpen={isQuickAddPopupOpen}
          image={
            selectedVariant?.img?.front
              ? {
                  src: selectedVariant?.img?.front,
                  alt: `${selectedVariant.name}`,
                }
              : null
          }
          onAddToCart={onAddToCart}
          onClose={() => {
            setIsQuickAddPopupOpen(false)
          }}
        />
      )}
    </ThemeProvider>
  )
}

interface QuickAddItemDesktopWrapperProps {
  title: string
  sizeGuideUrl: string | null
  sizeGuideText: string
  isOpen?: boolean
  sizes?: ProductSize[]
  isGiftCard?: boolean
  handle: Product['handle']
  onAddToCart?: (handle: Product['handle']) => void
  shouldOpenCartDrawerOnAddToCart: boolean
}

const QuickAddItemDesktopWrapper = ({
  title,
  sizeGuideUrl,
  sizeGuideText,
  isOpen = false,
  sizes = [],
  isGiftCard = false,
  onAddToCart,
  handle,
  shouldOpenCartDrawerOnAddToCart,
}: QuickAddItemDesktopWrapperProps) => {
  const [dynamicSizes, setDynamicSizes] = useState<ProductSize[]>(sizes)
  const [status, setStatus] = useState<Status>('idle')
  const [selectedSize, setSelectedSize] = useState<string | null>(null)
  const { addToCart, setIsDrawerOpen } = useCart()

  const handleSizeSelect = (id: string) => {
    setStatus('loading')
    setSelectedSize(id)

    addToCart(id, 1)
      .then(() => {
        setDynamicSizes((prevSizes) => {
          const updatedSizes = prevSizes.map((size) =>
            size.id === id
              ? {
                  ...size,
                  quantityAvailable: size.quantityAvailable - 1,
                }
              : size,
          )

          return updatedSizes
        })
        setStatus('success')
        setTimeout(() => {
          if (shouldOpenCartDrawerOnAddToCart) {
            window.location.pathname !== '/cart' && setIsDrawerOpen(true)
          }
        }, 1000)
      })
      .catch((error) => {
        handleError(error, { message: "Couldn't add product to cart." })
        setStatus('error')
      })
      .finally(() => {
        onAddToCart?.(handle)
        setTimeout(() => {
          setSelectedSize(null)
          setStatus('idle')
        }, 1000) // same duration as svg animation
      })
  }

  return (
    <SlideUp>
      <QuickAddToCartDesktop
        title={title}
        sizeGuideUrl={sizes.length > 1 ? sizeGuideUrl : null}
        sizeGuideText={sizeGuideText}
        sizes={dynamicSizes}
        status={status}
        selectedSize={selectedSize}
        isOpen={isOpen}
        isGiftCard={isGiftCard}
        onSizeSelect={handleSizeSelect}
      />
    </SlideUp>
  )
}
